import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from 'app/redux';
import {
  useGetContractorsQuery,
  useGetInvoiceSummariesQuery,
  useGetOtherRatesQuery,
  useGetPayrollDataByContractorQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import { WithLoadingOverlay } from 'common/components/LoadingSpinner';
import { useTimeframeSwitcherState } from 'common/components/TimeframeSwitcher/useTimeframeSwitcherState';
import { WizardDropdownOption } from 'common/components/WizardControls/WizardDropdown';
import { Contractor } from 'common/models';
import { DateOnlyFormat } from 'common/models/dateType';
import {
  mapOtherRates,
  OtherRateType,
} from 'common/models/settings/otherRates';
import { mobile } from 'common/styles/breakpoints';
import { blueShadeHeader } from 'common/styles/colors';
import { boxShadow } from 'common/styles/page';
import { FC, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Constants } from 'utils/constants';
import { createContractorOptions } from 'utils/formValues/dropdownOptions';
import { PayrollBody } from '../components/PayrollBody/PayrollBody';
import { PayrollHeader } from '../components/PayrollHeader/PayrollHeader';
import {
  PayrollSumTableItem,
  usePayrollSumTableData,
} from '../hooks/usePayrollSumTableData';
import {
  allContractorsCode,
  allContractorsLabel,
  PayrollPageState,
  payrollSlice,
  payrollSliceName,
} from '../PayrollSlice';

const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: ${boxShadow};
  padding: 20px 0;
  border-radius: 8px;
  width: 100%;

  h1 {
    margin: 0;
    font-family: KanitLight;
    font-size: 32px;
    color: ${blueShadeHeader};
  }

  h4 {
    margin: 0;
    padding-top: 10px;
    font-family: KanitSemiBold;
    font-size: 24px;
    color: ${blueShadeHeader};
  }

  @media (max-width: ${mobile}) {
    padding: 5px;
  }
`;

const NoData = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-items: center;
  with: 100%;
  height: 100px;
`;

/**
 * The params used in the URL for the 'Harvest Payroll' view.
 *
 * @remark
 *
 * The `code` param is needed in the url to handle cases where the contractor is
 * **inactive** and therefore not present in the dropdown options. We generate
 * the option using the contractor's code and manually set it in form state.
 */
export type PayrollUrlParams = {
  contractorId: string;
  code: string;
};

export const HarvestPayrollView: FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { contractorId, code } = useParams<PayrollUrlParams>();
  const { data: otherRates, error: otherRatesError } = useGetOtherRatesQuery();
  const {
    data: contractors,
    isLoading: isLoadingCrews,
    error: crewError,
  } = useGetContractorsQuery({ activeOnly: true });
  const { state: week } = useTimeframeSwitcherState('harvest-payroll', 'week');
  const { selectedContractor } = useAppSelector<PayrollPageState>(
    state => state[payrollSliceName],
  );
  const [inactiveCtr, setInactiveCtr] =
    useState<WizardDropdownOption<Contractor> | null>(null);

  const otherRatesMap = useMemo(
    () => mapOtherRates(otherRates || []),
    [otherRates],
  );

  const getCtrPayrollQueryParams = useMemo(() => {
    const contractorId = selectedContractor?.id || inactiveCtr?.value?.id;

    return contractorId
      ? { contractorId, startDate: week.monday.format(DateOnlyFormat) }
      : skipToken;
  }, [inactiveCtr, selectedContractor, week.monday]);

  const {
    data: payrollData,
    error: payrollDataError,
    isLoading,
    isFetching,
  } = useGetPayrollDataByContractorQuery(getCtrPayrollQueryParams, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: summaryData,
    error: summaryDataError,
    isLoading: isLoadingSummary,
    isFetching: isFetchingSummary,
  } = useGetInvoiceSummariesQuery(
    !selectedContractor?.id && selectedContractor?.code === allContractorsCode
      ? {
          startDate: week.monday.format(DateOnlyFormat),
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const { columns, data } = usePayrollSumTableData(summaryData || []);

  const tableProps: DataTableProps<PayrollSumTableItem> = {
    isLoading: false,
    isFetching: false,
    columns,
    data,
    onRowClick: row =>
      `${Constants.routes.HARVEST_PAYROLL}` +
      `/${row.contractorId}` +
      `/${row.contractorCode}`,
  };

  const allContractorsOption = useMemo(
    () => ({
      label: allContractorsLabel,
      value: { code: allContractorsCode } as Contractor,
    }),
    [],
  );

  const contractorOptions: WizardDropdownOption<Contractor>[] = useMemo(
    () => [allContractorsOption, ...createContractorOptions(contractors)],
    [allContractorsOption, contractors],
  );

  // Listen to the url changes and update the selected contractor accordingly.
  useEffect(() => {
    const parsedId = parseInt(contractorId, 10);
    const idIsNumber = parsedId && typeof parsedId === 'number';

    if (
      contractorOptions.length > 1 &&
      (contractorId === allContractorsCode || idIsNumber)
    ) {
      const contractor = contractorOptions?.find(
        option => option.value.id === parseInt(contractorId, 10),
      );

      if (contractor) {
        if (inactiveCtr) {
          setInactiveCtr(null);
        }
        dispatch(payrollSlice.actions.setContractor(contractor.value));
      } else if (idIsNumber) {
        const inactiveCtr: WizardDropdownOption<Contractor> = {
          label: code,
          value: {
            id: parsedId,
            code,
          } as Contractor,
        };

        setInactiveCtr(inactiveCtr);
        dispatch(payrollSlice.actions.setContractor(inactiveCtr.value));
      } else {
        if (inactiveCtr) {
          setInactiveCtr(null);
        }
        dispatch(
          payrollSlice.actions.setContractor(allContractorsOption.value),
        );
      }
    }
    // Omit the inactive contractor to avoid crash from endless re-rendering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allContractorsOption, code, contractorId, contractorOptions, dispatch]);

  // Clean up url.
  useEffect(() => {
    if (
      typeof parseInt(contractorId, 10) !== 'number' ||
      (contractorId === allContractorsCode && code !== allContractorsLabel) ||
      (contractorId !== allContractorsCode && code === allContractorsLabel)
    ) {
      history.replace(
        `${history.location.pathname}` +
          `/${allContractorsCode}` +
          `/${allContractorsLabel}`,
      );
    }
  }, [code, contractorId, history]);

  useEffect(() => {
    if (crewError) {
      handleError(crewError, 'Unable to load contractors.');
    }
    if (payrollDataError) {
      handleError(payrollDataError, "Unable to fetch contractor's payroll.");
    }
    if (summaryDataError) {
      handleError(
        payrollDataError,
        "Unable to fetch this week's payroll summary.",
      );
    }
    if (otherRatesError) {
      handleError(otherRatesError, "Unable to fetch 'other' rates.");
    }
  }, [crewError, otherRatesError, payrollDataError, summaryDataError]);

  return (
    <StyledContainer>
      <PayrollHeader
        summaryData={summaryData}
        payrollData={payrollData}
        contractorOptions={contractorOptions}
        isLoadingContractors={isLoadingCrews}
        isLoadingPayrollData={isLoading || isFetching}
        isLoadingSummary={isLoadingSummary || isFetchingSummary}
      />
      {selectedContractor?.id && (
        <WithLoadingOverlay isLoading={isLoading || isFetching}>
          {payrollData && payrollData.picks.length ? (
            <PayrollBody
              tarpRate={otherRatesMap.get(OtherRateType.Tarp)?.rate || 0}
              commissionRate={
                otherRatesMap.get(OtherRateType.Commission)?.rate || 0
              }
              payrollData={payrollData}
            />
          ) : (
            <NoData>
              No payroll information was found for this contractor.
            </NoData>
          )}
        </WithLoadingOverlay>
      )}
      {!selectedContractor?.id &&
        selectedContractor?.code === allContractorsCode && (
          <WithLoadingOverlay isLoading={isLoadingSummary || isFetchingSummary}>
            {summaryData && summaryData.length ? (
              <div style={{ margin: '10px 20px' }}>
                <DataTable<PayrollSumTableItem> {...tableProps} />
              </div>
            ) : (
              <NoData>No payroll information was found for this week.</NoData>
            )}
          </WithLoadingOverlay>
        )}
    </StyledContainer>
  );
};
